import React, { useEffect, useRef, useState } from "react";
import { Formik, Field, ErrorMessage, Form, FieldArray } from "formik";
import * as Yup from "yup";
import LoadCSSforfirstTime from "../../../../../CloudStorage/LoadCSSforfirstTime";
import FieldInput from "../../../../CustomComponents/FieldInput";
import ReactSelect from "react-select";
import CloudStorage from "../../../../../CloudStorage/CloudStorage";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import NumberInputBox from "../../../../CustomComponents/AllCustomeInput/NumberInputBox";
import {
  nomorethenhandread,
  removepresent,
} from "../../../../Common/FewCommonMethods";
import LoginLoaderButton from "../../../../Common/Loader/LoginLoaderButton";
import { titlevalidation } from "../../../../Common/commonvalidation";
import AsteriskLabel from "../../../../Common/AsteriskLabel";
import { isDurationLessThanMilestones } from "../../../../../utils/isDurationLessThanMilestones";
import { isPriceWithinBudget } from "../../../../../utils/isPriceWithinBudget";
import NumberInputBoxDuration from "../../../../CustomComponents/AllCustomeInput/NumberInputBoxDuration";
import useEnterKeySubmit from "../../../../../utils/useEnterKeySubmit";
import { penaltytips } from "../../../../Common/Tooltips/tooltip";

const formdatatomap = [
  {
    title: "Maximum project budget",
    name: "price",
    placeholder: "Enter Budget in $",
  },
  {
    title: (
      <>
        Project duration <AsteriskLabel />
      </>
    ),
    name: "duration_type",
    placeholder: "Project duration",
  },
];

const PDeadlineBudget = ({ formData, setFormData, nextPage, previousPage }) => {
  const [loading, setLoading] = useState(false);
  const path = "Assets/scss/FirstTimeUsers.module.scss";
  const formikRef = useRef(null);
  const [penaltyInputMode, setPenaltyInputMode] = useState({});

  const penaltylisting = useSelector(
    ({ categary: { penaltylist } }) => penaltylist
  );

  useEffect(() => {
    LoadCSSforfirstTime(path);
  }, [path]);

  // Initialize values
  const initialValues = {
    price: formData?.price,
    duration_type: formData?.duration_type || 2,
    duration: formData?.duration,
    milestones: formData?.milestones || [],
  };

  // Initialize penaltyInputMode based on initial values
  useEffect(() => {
    const initialMode = {};
    initialValues.milestones.forEach((milestone, index) => {
      initialMode[index] = milestone.penalty_id == 0;
    });
    setPenaltyInputMode(initialMode);
  }, []);

  const validationSchema = Yup.object().shape({
    price: Yup.number()
      .required("Maximum project budget is required")
      .moreThan(0, "Budget must be greater than 0")
      .typeError("Budget must be a number"),
    duration_type: Yup.string().required("Project duration is required"),
    duration: Yup.number()
      .required("Duration is required")
      .moreThan(0, "Duration must be greater than 0")
      .typeError("Duration must be a number"),
    milestones: Yup.array().of(
      Yup.object().shape({
        title: titlevalidation,
        duration: Yup.number()
          .typeError("Duration must be a number")
          .moreThan(0, "Duration must be greater than 0"),
        amount: Yup.number()
          .typeError("Amount must be a number")
          .moreThan(0, "Amount must be greater than 0"),
      })
    ),
  });

  // Helper function to convert duration to days
  const convertToDays = (duration, durationType) => {
    const type = Number(durationType);
    const d = Number(duration) || 0;
    if (type === 3) return d * 7; // weeks to days
    if (type === 4) return d * 30; // months to days (approx)
    return d; // already in days or other
  };

  // Updated handleSubmit with proper error placement
  const handleSubmit = async (values, formikHelpers) => {
    let hasErrors = false;
    const milestoneErrors = [];

    // Calculate project duration in days
    const projectDurationInDays = convertToDays(
      values.duration,
      values.duration_type
    );

    // Calculate total milestone amounts
    const totalMilestoneAmount = values.milestones.reduce((sum, milestone) => {
      return sum + (Number(milestone.amount) || 0);
    }, 0);

    // Unit label for error messages
    const projectUnitLabel =
      Number(values.duration_type) === 2 ? "day(s)" :
        Number(values.duration_type) === 3 ? "week(s)" :
          Number(values.duration_type) === 4 ? "month(s)" : "day(s)";

    // Check each milestone for individual errors
    values.milestones.forEach((milestone, index) => {
      const milestoneError = {};
      let milestoneErrorFound = false;

      // Check individual milestone duration
      if (milestone.duration && milestone.duration_type) {
        const milestoneDurationInDays = convertToDays(
          milestone.duration,
          milestone.duration_type
        );

        if (milestoneDurationInDays > projectDurationInDays) {
          milestoneError.duration = `Milestone duration cannot exceed total project duration`;
          milestoneErrorFound = true;
          hasErrors = true;
        }
      }

      // Check individual milestone amount
      if (milestone.amount && Number(milestone.amount) > Number(values.price)) {
        milestoneError.amount = `Milestone amount cannot exceed project budget`;
        milestoneErrorFound = true;
        hasErrors = true;
      }

      if (milestoneErrorFound) {
        milestoneErrors[index] = milestoneError;
      }
    });

    // Check total milestones duration vs project duration
    let totalMilestoneDuration = 0;
    values.milestones.forEach((milestone) => {
      if (milestone.duration && milestone.duration_type) {
        totalMilestoneDuration += convertToDays(
          milestone.duration,
          milestone.duration_type
        );
      }
    });

    if (totalMilestoneDuration > projectDurationInDays) {
      if (!milestoneErrors.length) milestoneErrors[0] = {};
      values.milestones.forEach((_, index) => {
        if (!milestoneErrors[index]) milestoneErrors[index] = {};
        if (!milestoneErrors[index].duration) {
          milestoneErrors[index].duration = "Total milestone duration exceeds project duration";
        }
      });
      hasErrors = true;
    }

    // Check total milestones amount vs project budget
    if (totalMilestoneAmount > values.price) {
      if (!milestoneErrors.length) milestoneErrors[0] = {};
      values.milestones.forEach((_, index) => {
        if (!milestoneErrors[index]) milestoneErrors[index] = {};
        if (!milestoneErrors[index].amount) {
          milestoneErrors[index].amount = "Total milestone amounts exceed project budget";
        }
      });
      hasErrors = true;
    }

    // Set all errors
    if (hasErrors) {
      formikHelpers.setErrors({ milestones: milestoneErrors });
      return;
    }

    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setFormData((prevData) => ({
      ...prevData,
      ...values,
      milestones: Array.isArray(values?.milestones) ? values?.milestones : "",
    }));

    nextPage();
    setLoading(false);
  };

  const handleProficiencyChange = (selectedOption, index, setFieldValue) => {
    setFieldValue(`milestones[${index}].penalty_id`, selectedOption.value);
    setFieldValue(
      `milestones[${index}].penalty`,
      removepresent(selectedOption.label)
    );
  };

  const handlePenaltyInputChange = (event, index, setFieldValue) => {
    setFieldValue(
      `milestones[${index}].penalty`,
      removepresent(event.target.value)
    );
    setFieldValue(`milestones[${index}].penalty_id`, 0);
  };

  const handleDeadlinetypeChange = (selectedOption, index, setFieldValue) => {
    setFieldValue(`milestones[${index}].duration_type`, selectedOption.value);
  };

  const handleNextClick = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const duretiontypeoptions = [
    { value: 2, label: "Days" },
    { value: 3, label: "Weeks" },
    { value: 4, label: "Months" },
  ];

  useEnterKeySubmit(handleNextClick);

  return (
    <div className="app-container">
      <div className="col-lg-10 col-md-11 mx-auto expert-flow">
        <div className="col-lg-10 mx-auto">
          <div className="row about-row">
            <div className="col-lg-4 about-left">
              <h6>
                03/04<span>Set deadline & Budget</span>
              </h6>
              <h2>Tell us about your budget & deadlines</h2>
              <p>Enter amount for the project</p>
            </div>

            <div className="col-lg-6 about-right">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={formikRef}
                enableReinitialize
              >
                {(formik) => (
                  <Form>
                    {formdatatomap.map((prop, key) => {
                      if (prop.name == "duration_type") {
                        return (
                          <div key={key} className="form-group">
                            <h6>{prop.title}</h6>
                            <div className="checkbox-label mb-3">
                              {[2, 3, 4].map((val) => (
                                <label key={val} className="checkbox-container">
                                  <Field
                                    type="radio"
                                    name={prop.name}
                                    value={val.toString()}
                                    checked={formik.values[prop.name] == val}
                                  />
                                  <span className="checkmark">
                                    {val == 2
                                      ? "Days"
                                      : val == 3
                                        ? "Weeks"
                                        : "Months"}
                                  </span>
                                </label>
                              ))}
                            </div>
                            <ErrorMessage
                              name={prop.name}
                              component="div"
                              className="error"
                            />
                            {formik.values.duration_type && (
                              <NumberInputBoxDuration
                                name="duration"
                                placeholder={`Enter number of ${formik.values.duration_type == 3
                                  ? "weeks"
                                  : formik.values.duration_type == 4
                                    ? "months"
                                    : "days"
                                  }`}
                              />
                            )}
                          </div>
                        );
                      }
                      return (
                        <NumberInputBox
                          title={prop.title}
                          name={prop.name}
                          placeholder={prop.placeholder}
                          key={key}
                        />
                      );
                    })}

                    {/* Milestones Section */}
                    <FieldArray name="milestones">
                      {({ push, remove }) => (
                        <>
                          <div className="form-group mb-0">
                            <h6>Add Milestones (Optional)</h6>
                            {formik.values.milestones.map(
                              (milestone, index) => (
                                <div key={index} className="milestone-section">
                                  <FieldInput
                                    title={
                                      <>
                                        Milestone {index + 1} Title{" "}
                                      </>
                                    }
                                    name={`milestones.${index}.title`}
                                    placeholder="Enter milestone title"
                                  />

                                  {/* Display milestone-specific errors */}
                                  {/* {formik.errors.milestones &&
                                    Array.isArray(formik.errors.milestones) &&
                                    formik.errors.milestones[index] && (
                                      <div className="milestone-errors"
                                      //  style={{
                                      //   backgroundColor: '#fff5f5',
                                      //   padding: '10px',
                                      //   marginBottom: '15px',
                                      //   borderRadius: '4px',
                                      //   border: '1px solid #fed7d7'
                                      // }}
                                      >
                                        {formik.errors.milestones[index].duration && (
                                          <div className="error" style={{ color: '#e53e3e', fontSize: '14px' }}>
                                            {formik.errors.milestones[index].duration}
                                          </div>
                                        )}
                                        {formik.errors.milestones[index].amount && (
                                          <div className="error" style={{ color: '#e53e3e', fontSize: '14px' }}>
                                            {formik.errors.milestones[index].amount}
                                          </div>
                                        )}
                                      </div>
                                    )} */}

                                  <div className="mile-flex">
                                    <div className="d-flex w-100 flex-wrap flex-input-mile">
                                      <div className="form-group">
                                        <label>
                                          Duration type
                                        </label>
                                        <ReactSelect
                                          options={duretiontypeoptions}
                                          placeholder="Select"
                                          value={duretiontypeoptions.find(
                                            (val) =>
                                              val.value ==
                                              formik.values.milestones[index]
                                                .duration_type
                                          )}
                                          onChange={(selectedOption) =>
                                            handleDeadlinetypeChange(
                                              selectedOption,
                                              index,
                                              formik.setFieldValue
                                            )
                                          }
                                        />
                                        <ErrorMessage
                                          name={`milestones.${index}.duration_type`}
                                          component="div"
                                          className="error"
                                        />
                                      </div>

                                      <NumberInputBoxDuration
                                        title={`Duration in ${formik.values.milestones[index]
                                          .duration_type == 2
                                          ? "day(s)"
                                          : formik.values.milestones[index]
                                            .duration_type == 3
                                            ? "week(s)"
                                            : "month(s)"
                                          }`}
                                        name={`milestones.${index}.duration`}
                                        placeholder="Duration"
                                        min="1"
                                        error={formik.errors.milestones &&
                                          Array.isArray(formik.errors.milestones) &&
                                          formik.errors.milestones[index]?.duration}
                                        showstar={false}
                                      />

                                      <NumberInputBox
                                        title="Price in $"
                                        name={`milestones.${index}.amount`}
                                        placeholder="Amount"
                                        error={formik.errors.milestones &&
                                          Array.isArray(formik.errors.milestones) &&
                                          formik.errors.milestones[index]?.amount}
                                        showstar={false}
                                      />

                                      <div className="form-group">
                                        <label>
                                          Penalty in % &nbsp;
                                          <img
                                            data-tooltip-id="my-tooltip-1"
                                            src={`${CloudStorage.cloudurl}/Assets/Images/common/info-circle.svg`}
                                            alt=""
                                          />
                                        </label>
                                        {penaltyInputMode[index] ? (
                                          <input
                                            type="text"
                                            onKeyDown={nomorethenhandread}
                                            name={`milestones.${index}.penalty`}
                                            placeholder="Enter penalty"
                                            onChange={(event) =>
                                              handlePenaltyInputChange(
                                                event,
                                                index,
                                                formik.setFieldValue
                                              )
                                            }
                                            value={
                                              formik.values.milestones[index]
                                                .penalty || ""
                                            }
                                          />
                                        ) : (
                                          <ReactSelect
                                            options={penaltylisting?.map(
                                              (penalty_id) => ({
                                                value: penalty_id.id,
                                                label: penalty_id.title,
                                              })
                                            )}
                                            placeholder="Select"
                                            value={penaltylisting
                                              .map((penalty_id) => ({
                                                value: penalty_id.id,
                                                label: penalty_id.title,
                                              }))
                                              .find(
                                                (val) =>
                                                  val.value ==
                                                  milestone.penalty_id
                                              )}
                                            onChange={(selectedOption) =>
                                              handleProficiencyChange(
                                                selectedOption,
                                                index,
                                                formik.setFieldValue
                                              )
                                            }
                                          />
                                        )}
                                        <ErrorMessage
                                          name={`milestones.${index}.penalty_id`}
                                          component="div"
                                          className="error"
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setPenaltyInputMode((prev) => ({
                                              ...prev,
                                              [index]: !prev[index],
                                            }))
                                          }
                                          className="buttons border-button mt-1"
                                        >
                                          {penaltyInputMode[index]
                                            ? "Use Select"
                                            : "Use Input"}
                                        </button>
                                      </div>
                                    </div>

                                    {formik.values.milestones.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          padding: '5px'
                                        }}
                                      >
                                        <img
                                          src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/delete-icon.svg`}
                                          alt="Delete milestone"
                                          width="20"
                                          height="20"
                                        />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )
                            )}

                            <button
                              type="button"
                              className="add-more-btn"
                              onClick={() => {
                                const milestones = formik.values.milestones;
                                if (
                                  milestones.length > 0 &&
                                  !milestones[milestones.length - 1].title.trim()
                                ) {
                                  return;
                                }
                                push({
                                  title: "",
                                  duration_type: "",
                                  duration: "",
                                  amount: "",
                                  penalty_id: "",
                                  penalty: "",
                                });
                              }}
                            >
                              Add more milestones
                            </button>
                          </div>
                        </>
                      )}
                    </FieldArray>
                    <button
                      type="submit"
                      style={{ display: "none" }}
                      ref={formikRef}
                    ></button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <div className="button-container col-lg-8 col-md-11 mx-auto">
        <button
          disabled={loading}
          type="button"
          onClick={previousPage}
          className="prev-button"
        >
          <img
            src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/back-arrow.svg`}
            alt="Back"
          />
          Back
        </button>
        <button type="button" onClick={handleNextClick} className="next-button">
          {loading ? (
            <LoginLoaderButton />
          ) : (
            <>
              Next
              <img
                src={`${CloudStorage.cloudurl}/Assets/Images/common/right-arrow.svg`}
                alt="Next"
              />
            </>
          )}
        </button>
      </div>

      <Tooltip
        id="my-tooltip-1"
        place="bottom"
        content={penaltytips}
      />
    </div>
  );
};

export default PDeadlineBudget;